import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiAward, FiUsers, FiHeart, FiStar, FiShield, FiClock } = FiIcons;

const About = () => {
  const stats = [
    { icon: FiAward, number: "15+", label: "Years Experience" },
    { icon: FiUsers, number: "5000+", label: "Happy Customers" },
    { icon: FiHeart, number: "1000+", label: "Custom Designs" },
    { icon: FiStar, number: "5.0", label: "Customer Rating" }
  ];

  const values = [
    {
      icon: FiShield,
      title: "Quality Assurance",
      description: "We guarantee the authenticity and quality of every piece with proper certification and lifetime warranty."
    },
    {
      icon: FiHeart,
      title: "Customer First",
      description: "Your satisfaction is our priority. We work closely with you to create jewelry that exceeds expectations."
    },
    {
      icon: FiAward,
      title: "Expert Craftsmanship",
      description: "Our skilled artisans bring decades of experience in creating stunning jewelry pieces."
    },
    {
      icon: FiClock,
      title: "Timeless Designs",
      description: "We create pieces that stand the test of time, combining classic elegance with modern aesthetics."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-yellow-400 to-yellow-500 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center text-white"
          >
            <h1 className="text-4xl md:text-6xl font-bold font-serif mb-6">
              About Red Rose Jewellery
            </h1>
            <p className="text-xl max-w-3xl mx-auto opacity-90">
              Crafting exceptional jewelry experiences in Dubai for over a decade
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-6"
            >
              <h2 className="text-4xl font-bold text-gray-800 font-serif">
                Our <span className="text-yellow-600">Story</span>
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                Red Rose Jewellery was founded with a simple vision: to create exceptional jewelry 
                that celebrates life's most precious moments. Located in the heart of Dubai Satwa, 
                we have been serving customers for over 15 years with dedication and passion.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Our journey began with a commitment to quality and craftsmanship. Today, we are 
                proud to be one of Dubai's trusted jewelry destinations, known for our exquisite 
                designs, personalized service, and unwavering commitment to excellence.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Every piece that leaves our store carries with it our promise of quality, 
                authenticity, and the love that goes into creating something truly special.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="grid grid-cols-2 gap-4"
            >
              <img
                src="https://i.ibb.co/1tP6cNm2/images-1.jpg"
                alt="Our jewelry store"
                className="w-full h-64 object-cover rounded-lg shadow-lg"
              />
              <img
                src="https://i.ibb.co/bR2gqndY/images-2.jpg"
                alt="Jewelry craftsmanship"
                className="w-full h-64 object-cover rounded-lg shadow-lg mt-8"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-800 font-serif mb-4">
              Our <span className="text-yellow-600">Achievements</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Numbers that reflect our commitment to excellence and customer satisfaction
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 to-yellow-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={stat.icon} className="w-8 h-8 text-white" />
                </div>
                <div className="text-3xl font-bold text-gray-800 mb-2">{stat.number}</div>
                <div className="text-gray-600">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-800 font-serif mb-4">
              Our <span className="text-yellow-600">Values</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              The principles that guide everything we do at Red Rose Jewellery
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="flex space-x-6 p-6 bg-gray-50 rounded-lg"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 to-yellow-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <SafeIcon icon={value.icon} className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-3">{value.title}</h3>
                  <p className="text-gray-600">{value.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-gradient-to-br from-yellow-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h2 className="text-4xl font-bold text-gray-800 font-serif mb-6">
              Meet Our <span className="text-yellow-600">Expert Team</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-12">
              Our skilled artisans and jewelry experts are passionate about creating beautiful 
              pieces and providing exceptional customer service.
            </p>
            
            <div className="bg-white rounded-lg shadow-lg p-8 max-w-2xl mx-auto">
              <div className="flex items-center justify-center space-x-4 mb-6">
                <div className="w-20 h-20 bg-gradient-to-br from-yellow-400 to-yellow-500 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-2xl font-serif">R</span>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-800">Red Rose Jewellery Team</h3>
                  <p className="text-yellow-600 font-medium">Expert Jewelry Craftsmen</p>
                </div>
              </div>
              <p className="text-gray-600 leading-relaxed">
                Our team brings together decades of experience in jewelry design, gemology, 
                and customer service. We are committed to helping you find or create the 
                perfect piece that reflects your unique style and celebrates your special moments.
              </p>
              <div className="mt-6 flex justify-center">
                <a
                  href="tel:+971508087073"
                  className="bg-gradient-to-r from-yellow-400 to-yellow-500 text-white px-6 py-3 rounded-lg font-medium hover:from-yellow-500 hover:to-yellow-600 transition-all duration-200"
                >
                  Speak with Our Experts
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;